import React, { useMemo } from 'react';
import {
  Modal,
  Alert,
  Button,
  Space,
  Typography,
  Collapse,
  Badge,
  Divider,
} from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  CalendarOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';

import moment from 'moment-timezone';
import { useTimezone } from '../../../../core/hooks/useTimezone';
import {
  buildLaunchReadinessReport,
} from '../CampaignRequestorUtil';

const { Text, Title } = Typography;
const { Panel } = Collapse;

/**
 * IssueRow
 * Renders a single issue with an icon and optional navigation link.
 */
const IssueRow = ({ issue, onNavigate }) => {
  const getIcon = () => {
    switch (issue.level) {
      case 'ORDER':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'CAMPAIGN':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'LINE_ITEM':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'CREATIVE':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'FLIGHT_SCHEDULE':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      default:
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
    }
  };

  const getBookmarkId = () => {
    if (issue.level === 'ORDER') {
      if (issue.field === 'trackingTags') return 'requester-attribution-reporting';
      if (issue.field === 'budget' || issue.field === 'cpm' || issue.field === 'dates' || issue.field === 'startDate' || issue.field === 'endDate') {
        return 'requester-duration-budget';
      }
      return 'requester-details';
    }
    if (issue.level === 'CAMPAIGN') {
      return `requester-campaign-${issue.campaignIndex ?? 0}`;
    }
    if (issue.level === 'LINE_ITEM' || issue.level === 'CREATIVE' || issue.level === 'FLIGHT_SCHEDULE') {
      // Try to infer campaign/line-item indices from the issue metadata
      if (issue.campaignIndex !== undefined && issue.lineItemIndex !== undefined) {
        return `requester-line-item-${issue.campaignIndex}-${issue.lineItemIndex}`;
      }
      if (issue.campaignIndex !== undefined) {
        return `requester-campaign-${issue.campaignIndex}`;
      }
      return `requester-campaign-0`;
    }
    return null;
  };

  const bookmarkId = getBookmarkId();

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
      <div style={{ marginTop: 2 }}>{getIcon()}</div>
      <div style={{ flex: 1 }}>
        <Text>{issue.message}</Text>
        {onNavigate && bookmarkId && (
          <div>
            <Button
              type="link"
              size="small"
              icon={<ArrowRightOutlined />}
              onClick={() => onNavigate(bookmarkId)}
              style={{ padding: 0, fontSize: 12 }}
            >
              Go to section
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * LaunchReadinessModal
 *
 * Displays a comprehensive launch-readiness report inside a modal.
 * Offers one-click date adjustment for past-date issues.
 */
const LaunchReadinessModal = ({
  visible,
  onClose,
  currentCampaignOrder,
  formValues,
  helpers,
  onNavigateToSection,
}) => {
  const { timezone } = useTimezone();

  // Merge live form values with server data so the report reflects what the
  // user actually sees on screen. Read campaigns directly from the form instance
  // to include unregistered fields (flightSchedule, adGroup, etc.) that
  // getFieldsValue() would drop.
  const dataForValidation = useMemo(() => {
    if (!currentCampaignOrder && !formValues) return null;
    const server = currentCampaignOrder || {};
    const form = formValues || {};
    // Read campaigns directly from the form instance to preserve unregistered fields
    const liveCampaigns = helpers?.form?.getFieldValue?.('campaigns') ?? form.campaigns ?? server.campaigns;
    return {
      ...server,
      // Editable fields — prefer live form state
      startDate: form.startDate ?? server.startDate,
      endDate: form.endDate ?? server.endDate,
      budget: form.budget ?? server.budget,
      cpm: form.cpm ?? server.cpm,
      category: form.category ?? server.category,
      awarenessCampaign: form.awarenessCampaign ?? server.awarenessCampaign,
      campaigns: liveCampaigns,
      // Server-managed fields stay from currentCampaignOrder
      // (advertiser, transactionsConfig, signUpConfig, sessionConfig, etc.)
    };
  }, [currentCampaignOrder, formValues, helpers.form]);

  const report = useMemo(() => {
    if (!dataForValidation) return null;
    return buildLaunchReadinessReport(dataForValidation);
  }, [dataForValidation]);

  const totalOrderIssues = report?.orderErrors?.length || 0;
  const totalCampaignIssues = report?.campaigns?.reduce(
    (sum, c) => sum + c.errors.length + c.dateErrors.filter((e) => !e.advisory).length,
    0
  ) || 0;
  const totalIssues = totalOrderIssues + totalCampaignIssues;
  const isUpdateMode = !!report?.isUpdateMode;

  // Detect specific issue types for conditional action buttons
  const allDateErrors = report?.campaigns?.flatMap((c) => c.dateErrors || []) || [];
  const hasPastDateErrors = allDateErrors.some((e) => e.message?.includes('in the past'));
  const hasFlightCoverageErrors = allDateErrors.some((e) => e.field === 'flightSchedule' && e.message?.includes('must cover all line items'));
  const hasAnyDateIssues = allDateErrors.length > 0;

  const handleAutoAdjust = (mode) => {
    if (!helpers?.form) return;
    try {
      // CRITICAL: read directly from the form store and preserve object references.
      // getFieldsValue() returns registered fields only (preserves refs).
      // Unregistered fields like flightSchedule must be pulled via getFieldValue.
      const allValues = helpers.form.getFieldsValue();
      // Use local calendar date for "today" to avoid UTC/local mismatch
      const todayStr = moment().format('YYYY-MM-DD');
      const today = moment().startOf('day');

      // Augment campaigns with unregistered flightSchedule fields
      (allValues.campaigns || []).forEach((campaign, cIdx) => {
        const cFlights = helpers.form.getFieldValue(['campaigns', cIdx, 'flightSchedule'])
          || helpers.form.getFieldValue(['campaigns', cIdx, 'flightSchedules'])
          || campaign.flightSchedule
          || campaign.flightSchedules
          || [];
        if (cFlights.length > 0) {
          campaign.flightSchedule = cFlights;
        }
        (campaign.lineItems || []).forEach((li, liIdx) => {
          const liFlights = helpers.form.getFieldValue(['campaigns', cIdx, 'lineItems', liIdx, 'flightSchedule'])
            || helpers.form.getFieldValue(['campaigns', cIdx, 'lineItems', liIdx, 'flightSchedules'])
            || li.flightSchedule
            || li.flightSchedules
            || [];
          if (liFlights.length > 0) {
            li.flightSchedule = liFlights;
          }
        });
      });

      // Collect all start dates (as simple date strings for comparison)
      const allStartDates = [];
      if (allValues.startDate) {
        allStartDates.push(moment(allValues.startDate).startOf('day'));
      }
      (allValues.campaigns || []).forEach((campaign) => {
        if (campaign?.startDate) allStartDates.push(moment(campaign.startDate).startOf('day'));
        const cFlights = campaign?.flightSchedule || campaign?.flightSchedules || [];
        cFlights.forEach((f) => { if (f?.startDate) allStartDates.push(moment(f.startDate).startOf('day')); });
        (campaign?.lineItems || []).forEach((li) => {
          if (li?.startDate) allStartDates.push(moment(li.startDate).startOf('day'));
          const liFlights = li?.flightSchedule || li?.flightSchedules || [];
          liFlights.forEach((f) => { if (f?.startDate) allStartDates.push(moment(f.startDate).startOf('day')); });
        });
      });

      let earliestStart = null;
      allStartDates.forEach((d) => {
        if (!earliestStart || d.isBefore(earliestStart)) earliestStart = d;
      });
      if (!earliestStart || earliestStart.format('YYYY-MM-DD') >= todayStr) {
        onClose();
        return;
      }
      const daysToShift = today.diff(earliestStart, 'days');

      // For 'preserve-duration': shift ALL dates forward by daysToShift
      const shiftDate = (dateValue) => {
        if (!dateValue) return null;
        return moment(dateValue).add(daysToShift, 'days').startOf('day');
      };

      // For 'shorten': move PAST start dates to today; leave future dates & all ends unchanged.
      // This matches the button label "Moves start dates to today. End dates stay the same."
      const movePastStartToToday = (startValue) => {
        if (!startValue) return null;
        const startMoment = moment(startValue).startOf('day');
        // If already today or in the future, leave it alone
        if (startMoment.format('YYYY-MM-DD') >= todayStr) return startValue;
        return today.clone();
      };

      const adjustStart = mode === 'shorten' ? movePastStartToToday : shiftDate;
      const adjustEnd = mode === 'shorten' ? (d) => d : shiftDate;

      // Update order-level dates
      const orderUpdates = {};
      if (allValues.startDate) orderUpdates.startDate = adjustStart(allValues.startDate, allValues.endDate);
      if (allValues.endDate) orderUpdates.endDate = adjustEnd(allValues.endDate);

      // Update campaigns by spreading EXISTING objects (preserves AntD internals)
      const updatedCampaigns = (allValues.campaigns || []).map((campaign) => {
        const updatedCampaign = { ...campaign };
        if (campaign.startDate) updatedCampaign.startDate = adjustStart(campaign.startDate, campaign.endDate);
        if (campaign.endDate) updatedCampaign.endDate = adjustEnd(campaign.endDate);

        // Campaign flight schedules — spread existing flight objects to preserve refs
        const cFlights = campaign.flightSchedule || campaign.flightSchedules || [];
        if (cFlights.length > 0) {
          const shiftedFlights = cFlights.map((f) => ({
            ...f,
            startDate: f.startDate ? adjustStart(f.startDate, f.endDate) : null,
            endDate: f.endDate ? adjustEnd(f.endDate) : null,
          }));
          updatedCampaign.flightSchedule = shiftedFlights;
          updatedCampaign.flightSchedules = shiftedFlights;
        }

        // Line items
        if (campaign.lineItems && Array.isArray(campaign.lineItems)) {
          updatedCampaign.lineItems = campaign.lineItems.map((li) => {
            const updatedLi = { ...li };
            if (li.startDate) updatedLi.startDate = adjustStart(li.startDate, li.endDate);
            if (li.endDate) updatedLi.endDate = adjustEnd(li.endDate);
            const liFlights = li.flightSchedule || li.flightSchedules || [];
            if (liFlights.length > 0) {
              updatedLi.flightSchedule = liFlights.map((f) => ({
                ...f,
                startDate: f.startDate ? adjustStart(f.startDate, f.endDate) : null,
                endDate: f.endDate ? adjustEnd(f.endDate) : null,
              }));
            }
            return updatedLi;
          });
        }
        return updatedCampaign;
      });

      helpers.form.setFieldsValue({ ...orderUpdates, campaigns: updatedCampaigns });
      if (helpers.syncFormValues) helpers.syncFormValues();
      onClose();
    } catch (error) {
      console.error('Auto-adjust error:', error);
    }
  };

  /**
   * Extend the last campaign flight's end date to cover the latest line item.
   * Reads campaigns via getFieldValue('campaigns') to preserve ALL fields
   * (including unregistered ones like flightSchedule), only mutates the
   * last flight's endDate, then writes back the full array.
   */
  const handleExtendLastFlight = () => {
    if (!helpers?.form) return;
    try {
      // CRITICAL: use getFieldValue('campaigns') not getFieldsValue().
      // getFieldsValue() only returns registered fields and drops
      // unregistered ones like flightSchedule, adGroup details, etc.
      const campaigns = helpers.form.getFieldValue('campaigns') || [];
      let changed = false;

      const updatedCampaigns = campaigns.map((campaign, cIdx) => {
        if (!campaign) return campaign;

        const flights = campaign.flightSchedule || campaign.flightSchedules || [];
        if (flights.length === 0) return campaign;

        const lineItemEnds = (campaign.lineItems || [])
          .filter((li) => li.markedForDelivery !== false)
          .map((li) => li.endDate)
          .filter(Boolean)
          .map((d) => moment(d).startOf('day'));

        if (lineItemEnds.length === 0) return campaign;

        const latestLineItemEnd = lineItemEnds.reduce((latest, d) => {
          return (!latest || d.isAfter(latest)) ? d : latest;
        }, null);

        const lastFlight = flights[flights.length - 1];
        const lastFlightEnd = moment(lastFlight.endDate).startOf('day');

        if (latestLineItemEnd && lastFlightEnd.isBefore(latestLineItemEnd)) {
          changed = true;
          const updatedFlights = [...flights];
          updatedFlights[updatedFlights.length - 1] = {
            ...lastFlight,
            endDate: latestLineItemEnd.clone(),
          };
          return { ...campaign, flightSchedule: updatedFlights };
        }
        return campaign;
      });

      if (changed) {
        helpers.form.setFieldsValue({ campaigns: updatedCampaigns });
        if (helpers.syncFormValues) helpers.syncFormValues();
      }
      onClose();
    } catch (error) {
      console.error('Extend last flight error:', error);
    }
  };

  if (!report) return null;

  return (
    <Modal
      title={
        <Space>
          <Text strong style={{ fontSize: 16 }}>{isUpdateMode ? 'Update Readiness Check' : 'Launch Readiness Check'}</Text>
          {report.isValid ? (
            <Badge status="success" text="Ready" />
          ) : (
            <Badge status="error" text={`${totalIssues} issue(s)`} />
          )}
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={
        <Button onClick={onClose}>Close</Button>
      }
      width={720}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', padding: '16px 24px' }}
    >
      {/* Overall status */}
      {report.isValid ? (
        <Alert
          message={isUpdateMode ? 'All clear — this update is ready to submit.' : 'All clear — this campaign order is ready for launch.'}
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Alert
          message={`${totalIssues} issue(s) ${isUpdateMode ? 'blocking this update.' : 'blocking launch.'}`}
          description="Fix the issues below, then re-run this check."
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Order-level issues */}
      {report.orderErrors.length > 0 && (
        <>
          <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
            Campaign Order
          </Title>
          {report.orderErrors.map((issue, idx) => (
            <IssueRow
              key={`order-${idx}`}
              issue={{ ...issue, campaignIndex: undefined, lineItemIndex: undefined }}
              onNavigate={onNavigateToSection}
            />
          ))}
          <Divider style={{ margin: '16px 0' }} />
        </>
      )}

      {/* Per-campaign issues */}
      {report.campaigns.length > 0 && (
        <Collapse
          defaultActiveKey={report.campaigns
            .filter((c) => !c.isValid)
            .map((c) => String(c.campaignIndex))}
          ghost
          style={{ background: 'transparent' }}
        >
          {report.campaigns.map((campaign) => {
            const campaignIssueCount = campaign.errors.length + campaign.dateErrors.filter((e) => !e.advisory).length;
            const header = (
              <Space>
                <Text strong>{campaign.campaignName}</Text>
                {campaign.isValid ? (
                  <Badge status="success" text="Ready" />
                ) : (
                  <Badge status="error" text={`${campaignIssueCount} issue(s)`} />
                )}
              </Space>
            );

            return (
              <Panel
                key={String(campaign.campaignIndex)}
                header={header}
                style={{ marginBottom: 8 }}
              >
                {campaign.errors.map((issue, idx) => (
                  <IssueRow
                    key={`camp-${campaign.campaignIndex}-err-${idx}`}
                    issue={{ ...issue, campaignIndex: campaign.campaignIndex }}
                    onNavigate={onNavigateToSection}
                  />
                ))}
                {campaign.dateErrors.filter((e) => !e.advisory).map((issue, idx) => (
                  <IssueRow
                    key={`camp-${campaign.campaignIndex}-date-${idx}`}
                    issue={{ ...issue, level: 'FLIGHT_SCHEDULE', campaignIndex: campaign.campaignIndex }}
                    onNavigate={onNavigateToSection}
                  />
                ))}
                {campaign.dateErrors.some((e) => e.advisory) && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ margin: '8px 0' }}
                    message="Date notes (advisory — won't block this update)"
                    description={
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {campaign.dateErrors.filter((e) => e.advisory).map((issue, idx) => (
                          <li key={`camp-${campaign.campaignIndex}-adv-${idx}`}>{issue.message}</li>
                        ))}
                      </ul>
                    }
                  />
                )}
              </Panel>
            );
          })}
        </Collapse>
      )}

      {/* Quick-fix action buttons — only show relevant ones */}
      {hasAnyDateIssues && (
        <>
          <Divider style={{ margin: '16px 0' }} />

          {/* Past date fix — never offered for an in-flight campaign (shifting
              historical start dates would corrupt its served schedule). */}
          {hasPastDateErrors && !isUpdateMode && (
            <>
              <Alert
                message="Past dates detected"
                description="Some dates are in the past. Use the buttons below to shift them forward."
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
              />
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  type="primary"
                  icon={<CalendarOutlined />}
                  onClick={() => handleAutoAdjust('shorten')}
                  block
                >
                  Shift Starts Only (shortens campaign)
                </Button>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Moves past start dates to today. End dates stay the same.
                </Text>

                <Button
                  type="default"
                  icon={<CalendarOutlined />}
                  onClick={() => handleAutoAdjust('preserve-duration')}
                  block
                >
                  Preserve Duration
                </Button>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Shifts all dates forward equally. Campaign runs for the same number of days.
                </Text>
              </Space>
            </>
          )}

          {/* Flight coverage fix */}
          {hasFlightCoverageErrors && (
            <>
              <Alert
                message="Flight schedule coverage gap"
                description="The last campaign flight ends before the latest line item. Extend it to cover all line items."
                type="warning"
                showIcon
                style={{ marginTop: hasPastDateErrors ? 12 : 0, marginBottom: 12 }}
              />
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  type="primary"
                  icon={<CalendarOutlined />}
                  onClick={handleExtendLastFlight}
                  block
                >
                  Extend Last Flight to Cover Line Items
                </Button>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Extends the last campaign flight's end date to match the latest line item end date.
                </Text>
              </Space>
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default LaunchReadinessModal;
